'use client';

import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import {
	addTransaction,
	getAvailableVouchers,
	getHistory,
	getNewVersionInfo,
	getRankingInfo,
	getRewardInfo,
	getRewardLabels,
	getSabaCoinRecord,
	getSabaCoinEventInfo,
	redeemCashWithSabaCoin,
	getVoucherHistory,
	getVoucherStock,
	purchaseVoucher,
	receiveWeeklyAward,
	updateEventProgress,
	getCustomerPrizeInfo,
	isPrizeInfoNoticed,
	getSabaCoinRewardLevelsInfo,
	isJackPotPrizeList
} from '@/hooks/query/use-reward-system';
import { fetchGetMarketByLeagueOrMatch } from '@/hooks/use-galaxy-deeplink-params';
import { OnKafkaSetProps, UpdateEventProgress } from '@nf/achievement-system-v2/src/types';
import { AccountActStatus } from '@nf/utils-common/constants';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@/navigation';
import dynamic from 'next/dynamic';
import { Bai_Jamjuree } from '@/lib/fonts';
import { useSportHref } from '@/hooks/query';
import { useLocale } from 'next-intl';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { profilePanelService } from '@/services/profile-panel.service';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { modalService } from '@/services/modal.service';
import { useSiteStore } from '@/store/site';
import { getAuthToken } from '@/store/auth';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { cloudUrlParser } from '@/utils/aws-domain-util';
const RewardProvider = dynamic(() => import('@nf/achievement-system-v2').then(m => m.RewardProvider));

export const RewardWrapperProvider = ({ children }: { children: React.ReactNode }) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { apiDomain } = useSiteStore.getState();
	const authToken = getAuthToken();
	const view = useAppearanceStore(state => state.view);
	const { createSportHref } = useSportHref();
	const locale = useLocale();
	const { user } = useAccountInfo();
	const router = useRouter();
	const queryClient = useQueryClient();
	const redirectToFirstLiveSport = () => {
		profilePanelService.displayProfilePanel(false);
		router.push('/live');
	};

	const redirectToLeague = async ({ sport, leagueId }: { sport: number, leagueId: number }) => {
		profilePanelService.displayProfilePanel(false);
		try {
			const market = await fetchGetMarketByLeagueOrMatch({
				apiDomain: apiDomain.ctcdDomain,
				market: 'l',
				leagueId,
				matchId: 0,
				authToken
			});

			// If the leagueId represents the AFF Championship, redirect to the "World" section.
			if (sport === 1 && leagueId === 155467) {
				router.push(`/world/aff-cup/1/${market}`);
				return
			} else {
				let path = matchEquals(market)
					.on('l', () => `/live/${sport}`)
					.otherwise(() => `/sports/${sport}/${market}`);
				path = `${path}?filterLeagueId=${leagueId}`;
				router.push(path);
			}
		} catch (error) {
			console.error(`fetch market error: ${error}`);
		}
	};

	const redirectToEuroCup = () => {
		const destination = createSportHref('/world/euro-cup/1/e');
		profilePanelService.displayProfilePanel(false);
		router.push(destination, { locale });
	};

	const goToGalaxySurvey = () => {
		document.dispatchEvent(new CustomEvent('show-player-survey-form', {}));
	};

	const updateEventProgressWithQueryClient = (rewardId: number): Promise<UpdateEventProgress> => {
		return updateEventProgress(user, queryClient, rewardId);
	};

	const purchaseVoucherWithQueryClient = (voucherDetailId: number): Promise<void> => {
		return purchaseVoucher(user, queryClient, voucherDetailId);
	};

	const receiveWeeklyAwardWithQueryClient = (): Promise<void> => {
		return receiveWeeklyAward(user, queryClient);
	};

	const setOddsBoostParticipantsNotify = async (): Promise<void> => {
		try {
			await fetchComposer.postWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/api/PromotionOddsBoost/SetOddsBoostParticipantsNotify`))(authToken);
		} catch (error) {
			console.log(error);
		}
	};

	const getImageSource = (path: string): string => {
		return path;
	}
	const setKafka = (data: OnKafkaSetProps) => {
		const kafkaData = {
			note: data.note ? data.note : KafkaNote.Reward,
			type: data.type,
			action: data.action
		}
		setKafkaNormal(kafkaData);
	}

	const onOpenRewardNickNameModal = () => {
		if (user?.Niname) {
			setKafka({
				type: 8,
				action: view === ViewEnum.mars ? 7 : 3
			})
		}
		profilePanelService.displayProfilePanel(true);
		modalService.openNickNameModal(true, true);
	};
	const voucherCondition = {
		enableExchange: true,
		enableOddsBooster: true,
		enableRiskFree: user?.RewardInfo.EnableRewardRiskFreeBet != undefined ? user.RewardInfo.EnableRewardRiskFreeBet : true,
		enableNicknameChange: user?.RewardInfo.EnableRewardNicknameChange != undefined ? user.RewardInfo.EnableRewardNicknameChange : true,
		enableNewRiskFreeBet: user?.RewardInfo.EnableRewardNewRiskFreeBet != undefined ? user.RewardInfo.EnableRewardNewRiskFreeBet : true,
	};

	const siteId = SiteConfigsData?.GetMessageID ?? '0';

	return user?.ActStatus === AccountActStatus.Deposited ? (
		<RewardProvider
			fontClassName={Bai_Jamjuree.className}
			lang={user?.Lang || ''}
			id={user?.ID || 0}
			UserNickName={user?.Niname || ''}
			currencyName={user?.LicenseeSiteSetting?.DisplayCurrencyName || ''}
			siteId={siteId}
			platform={view === ViewEnum.mars ? 'Mars' : 'Galaxy'}
			hasReward={Boolean(user?.RewardInfo?.EnableRewardSystem)}
			imageServerDomain={SiteConfigsData?.EnableImageServer ? SiteConfigsData?.ImageServerUrl : undefined}
			enableCoinEvent={Boolean(SiteConfigsData?.EnableSabaCoinFlow)}
			enableCoinEventUpdate={Boolean(SiteConfigsData?.EnableNewSabaCoinFlow)}
			enableCoinRewardLevel={Boolean(SiteConfigsData?.EnableCoinRewardLevel)}
			getSabaCoinRewardLevelsInfo={getSabaCoinRewardLevelsInfo}
			voucherCondition={voucherCondition}
			sabaCoinWallet={user?.SabaCoinWallet}
			promotionOddsBoostInfo={user?.PromotionOddsBoostInfo}
			enableSingleMatchJackpot={Boolean(SiteConfigsData?.EnableSingleMatchJackpot)}
			onTransactSuccess={() => { }}
			onKafkaSet={setKafka}
			goToSport={redirectToFirstLiveSport}
			goToGalaxySport={redirectToFirstLiveSport}
			goToGalaxySurvey={goToGalaxySurvey}
			goToParlay={redirectToFirstLiveSport}
			goToEuroCup={(user.EnableLicBooked && user?.RewardInfo.EnableRewardSystem) ? redirectToEuroCup : undefined}
			goToStreamer={redirectToFirstLiveSport}
			onStatementOpen={() => router.push('/bets/s?select=promotion')}
			getImageSource={getImageSource}
			getRewardInfo={getRewardInfo}
			getSabaCoinEventInfo={getSabaCoinEventInfo}
			redeemCashWithSabaCoin={redeemCashWithSabaCoin}
			getRewardLabels={getRewardLabels}
			getHistory={getHistory}
			updateEventProgress={updateEventProgressWithQueryClient}
			addTransaction={addTransaction}
			getRankingInfo={getRankingInfo}
			getVoucherStock={getVoucherStock}
			purchaseVoucher={purchaseVoucherWithQueryClient}
			getAvailableVouchers={getAvailableVouchers}
			getVoucherHistory={getVoucherHistory}
			getSabaCoinRecord={getSabaCoinRecord}
			receiveWeeklyAward={receiveWeeklyAwardWithQueryClient}
			setOddsBoostParticipantsNotify={setOddsBoostParticipantsNotify}
			getNewVersionInfo={getNewVersionInfo}
			getCustomerPrizeInfo={getCustomerPrizeInfo}
			isPrizeInfoNoticed={isPrizeInfoNoticed}
			onGoToLeague={redirectToLeague}
			onStatementPromotionOpen={() => {
				router.push('/bets/s?select=promotion');
			}}
			onStatementSabaCoinOpen={() => {
				router.push('/bets/s?select=sabacoin');
			}}
			onOpenRewardNickNameModal={onOpenRewardNickNameModal}
			onSwitchView={() => {}}
			onJackpotPrizePageOpen={() => {
				router.push('/promotion/jackpot');
			}}
			isJackPotPrizeList={isJackPotPrizeList}
		>
			{children}
		</RewardProvider>
	) : children;
}